import { useCallback, useEffect, useRef, useState } from 'react';

import { useFetcher } from '@remix-run/react';
import type { Pagination } from '@shared/dream-components';

import type { ProductReviewsResponse } from '~/interfaces/product';

interface Arguments {
  defaultReviews: ProductReviewsResponse['reviews'];
  pagination: Pagination | null;
  productSlug: string;
  perPage?: number;
  enabled?: boolean;
}

const usePaginatedProductReviews = ({
  defaultReviews = [],
  pagination,
  productSlug,
  perPage,
  enabled = true,
}: Arguments) => {
  const fetcher = useFetcher<ProductReviewsResponse>();
  const fetchedData = fetcher?.data;
  const fetchedPagination = fetchedData?.pagination;

  const [reviews, setReviews] = useState<ProductReviewsResponse['reviews']>(defaultReviews);
  const [currentPage, setCurrentPage] = useState((fetchedPagination ? fetchedPagination.page : pagination?.page) || 0);
  const [totalPages, setTotalPages] = useState(
    (fetchedPagination ? fetchedPagination.total_pages : pagination?.total_pages) || 0,
  );
  const [totalReviews] = useState((fetchedPagination ? fetchedPagination.total : pagination?.total) || 0);
  const isLoadingMore = useRef(false);
  const hasInitialized = useRef(false);

  // Sync initial state when clientLoader data arrives (runs once)
  useEffect(() => {
    if (!hasInitialized.current && defaultReviews.length > 0 && pagination) {
      setReviews(defaultReviews);
      setCurrentPage(pagination.page);
      setTotalPages(pagination.total_pages);
      hasInitialized.current = true;
    }
  }, [defaultReviews, pagination]);

  const loadMoreReviews = useCallback(() => {
    if (!enabled) return;

    isLoadingMore.current = true;

    const searchParams = new URLSearchParams();
    searchParams.append('slug', productSlug);
    searchParams.append('page', String(currentPage + 1));
    if (perPage) {
      searchParams.append('per_page', String(perPage));
    }

    fetcher.load(`/product_reviews?${searchParams.toString()}`);
  }, [currentPage, fetcher, perPage, enabled, productSlug]);

  // Update state when data is fetched
  // biome-ignore lint/correctness/useExhaustiveDependencies: Only re-run when new data arrives to avoid race conditions
  useEffect(() => {
    if (!fetchedData) return;
    // Only process if we initiated a load more
    if (!isLoadingMore.current) return;

    const fetchedPage = fetchedData?.pagination?.page;
    const newReviews = fetchedData?.reviews || [];

    // Append reviews for load more functionality
    setReviews((prev) => prev.concat(newReviews));
    setCurrentPage(fetchedPage || currentPage + 1);
    setTotalPages(fetchedData?.pagination?.total_pages || totalPages);
    isLoadingMore.current = false;
  }, [fetchedData]);

  return {
    reviews,
    showLoadMore: currentPage + 1 <= totalPages,
    isLoading: fetcher?.state !== 'idle',
    loadMoreReviews,
    currentPage,
    totalPages,
    totalReviews,
  };
};

export default usePaginatedProductReviews;
